import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell,
  Send,
  Users,
  Clock,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

const AdminNotifications = () => {
  const [notificationType, setNotificationType] = useState('broadcast');
  const [targetAudience, setTargetAudience] = useState('all');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);

  const audiences = [
    { value: 'all', label: 'All Users', icon: Users, description: 'Send to all registered users' },
    { value: 'free_users', label: 'Free Tier Users', icon: Users, description: 'Users on free plan' },
    { value: 'paid_users', label: 'Paid Users', icon: Users, description: 'Users with active subscriptions' },
    { value: 'expiring_7days', label: 'Expiring in 7 Days', icon: Clock, description: 'Subscriptions expiring soon' },
    { value: 'expiring_3days', label: 'Expiring in 3 Days', icon: AlertTriangle, description: 'Urgent renewal reminders' },
    { value: 'expired', label: 'Expired Subscriptions', icon: AlertTriangle, description: 'Users with expired plans' },
  ];

  const handleSendNotification = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (message.length > 160) {
      toast.error('SMS messages should be under 160 characters for best delivery');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/notifications/send`, 
        {
          notification_type: notificationType,
          target_audience: targetAudience,
          message: message.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Notification sent successfully');
      setMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleTestNotification = async () => {
    if (!testPhone.trim()) {
      toast.error('Enter a phone number for test');
      return;
    }
    if (!message.trim()) {
      toast.error('Please enter a message to test');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/notifications/test`,
        {
          phone: testPhone.trim(),
          message: message.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || 'Test notification sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Send Notifications</h1>
          <p className="text-slate-400">Send SMS notifications to users</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Compose */}
          <div className="lg:col-span-2 space-y-6">
            {/* Target Audience */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Select Target Audience</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {audiences.map((audience) => (
                  <button
                    key={audience.value}
                    onClick={() => setTargetAudience(audience.value)}
                    className={`flex items-start gap-3 p-4 rounded-xl border transition-all text-left ${
                      targetAudience === audience.value
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                    data-testid={`audience-${audience.value}`}
                  >
                    <div className={`p-2 rounded-lg ${
                      targetAudience === audience.value
                        ? 'bg-amber-500/20 text-amber-500'
                        : 'bg-slate-700 text-slate-400'
                    }`}>
                      <audience.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`font-medium ${
                        targetAudience === audience.value ? 'text-amber-500' : 'text-white'
                      }`}>
                        {audience.label}
                      </p>
                      <p className="text-sm text-slate-400">{audience.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Compose Message</h3>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your notification message..."
                rows={4}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                data-testid="notification-message"
              />
              <div className="flex items-center justify-between mt-3">
                <span className={`text-sm ${message.length > 160 ? 'text-red-400' : 'text-slate-400'}`}>
                  {message.length}/160 characters
                </span>
                {message.length > 160 && (
                  <span className="text-sm text-yellow-400">
                    Long messages may be split into multiple SMS
                  </span>
                )}
              </div>
            </div>

            {/* Send Button */}
            <button
              onClick={handleSendNotification}
              disabled={sending || !message.trim()}
              className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold py-4 rounded-xl transition-all disabled:opacity-50"
              data-testid="send-notification-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Notification
                </>
              )}
            </button>

            {/* Test Notification */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Send Test</h3>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Phone number (10 digits)"
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="test-phone"
              />
              <button
                onClick={handleTestNotification}
                disabled={testing || !testPhone.trim() || !message.trim()}
                className="w-full mt-4 flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50"
                data-testid="test-notification-btn"
              >
                {testing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Test
                  </>
                )}
              </button>
              <p className="text-xs text-slate-400 mt-3">Uses the message above and sends to one number only.</p>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4">Preview</h3>
            <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-slate-400">SMS Preview</span>
              </div>
              <p className="text-white text-sm whitespace-pre-wrap">
                {message ? `sideo: ${message}` : 'Your message will appear here...'}
              </p>
            </div>

            <div className="mt-6 space-y-3">
              <h4 className="text-white font-medium">Sending to:</h4>
              <div className="flex items-center gap-2 text-slate-300">
                <div className="w-2 h-2 bg-amber-500 rounded-full" />
                {audiences.find(a => a.value === targetAudience)?.label || 'Select audience'}
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
              <p className="text-yellow-400 text-sm">
                <strong>Note:</strong> SMS will be sent to up to 500 recipients per batch. 
                Twilio charges apply for each SMS sent.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNotifications;
